import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { AvailableCoupon } from "@/hooks/useBookingCoupons";
import { PenaltyPaymentModal } from "@/components/booking/PenaltyPaymentModal";
import { usePenaltyBalance } from "@/hooks/usePenaltyPayment";
import { useToast } from "@/hooks/use-toast";
interface TimeSlot {
  id: string;
  start_time: string;
  womens_only?: boolean;
}
interface BookingSummaryProps {
  selectedService?: {
    name: string;
    duration_minutes: number;
    price: number;
    provider_id: string;
  };
  provider?: {
    name: string;
  };
  selectedDate?: Date;
  selectedTimeSlot: string;
  availableTimeSlots?: TimeSlot[];
  selectedMembershipId: string | null;
  selectedCoupon: AvailableCoupon | null;
  couponDiscount: number;
  promotionDiscount?: number;
  activePromotion?: any;
  finalPrice: number;
  hasActiveWaivers: boolean;
  waiversCompleted: boolean;
  canBookWithMembership: boolean;
  canBookRegularly: boolean;
  isFreeBooking: boolean;
  processing: boolean;
  onBookingWithMembership: () => void;
  onFreeBooking: () => void;
  onRegularBooking: () => void;
}
const BookingSummary = ({
  selectedService,
  provider,
  selectedDate,
  selectedTimeSlot,
  availableTimeSlots,
  selectedMembershipId,
  selectedCoupon,
  couponDiscount,
  promotionDiscount = 0,
  activePromotion,
  finalPrice,
  hasActiveWaivers,
  waiversCompleted,
  canBookWithMembership,
  canBookRegularly,
  isFreeBooking,
  processing,
  onBookingWithMembership,
  onFreeBooking,
  onRegularBooking
}: BookingSummaryProps) => {
  const [penaltyModalOpen, setPenaltyModalOpen] = useState(false);
  const {
    data: penaltyBalance,
    isLoading: penaltyLoading
  } = usePenaltyBalance();
  const {
    toast
  } = useToast();

  // Only consider it a penalty balance if it's actually greater than 0
  const hasPenaltyBalance = penaltyBalance && penaltyBalance > 0;
  const isDateTimeSelected = selectedDate && selectedTimeSlot;
  const handleBookingAttempt = (bookingType: 'membership' | 'free' | 'regular') => {
    // Check if date and time slot are selected first
    if (!selectedDate || !selectedTimeSlot) {
      toast({
        title: "Missing Selection",
        description: "You haven't selected a date and time slot for your booking",
        variant: "destructive",
        duration: 3000
      });
      return;
    }
    if (hasPenaltyBalance) {
      setPenaltyModalOpen(true);
      return;
    }

    // Proceed with normal booking
    switch (bookingType) {
      case 'membership':
        onBookingWithMembership();
        break;
      case 'free':
        onFreeBooking();
        break;
      case 'regular':
        onRegularBooking();
        break;
    }
  };

  // Wrapper function that handles both disabled state clicks and enabled clicks
  const handleButtonClick = (bookingType: 'membership' | 'free' | 'regular', event: React.MouseEvent) => {
    event.preventDefault();
    handleBookingAttempt(bookingType);
  };
  const getDisplayTime = () => {
    if (!selectedTimeSlot || !availableTimeSlots) return null;
    const timeSlot = availableTimeSlots.find(slot => slot.id === selectedTimeSlot);
    if (!timeSlot) return null;

    // Parse UTC time from database and convert to local time for display
    const startTime = new Date(timeSlot.start_time);
    return format(startTime, "HH:mm");
  };
  const getSelectedTimeSlot = () => {
    if (!selectedTimeSlot || !availableTimeSlots) return null;
    return availableTimeSlots.find(slot => slot.id === selectedTimeSlot);
  };
  const selectedTimeSlotData = getSelectedTimeSlot();
  return <>
      <Card className="sticky top-6">
        <CardHeader>
          <CardTitle>Booking Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium">{selectedService?.name || "Service"}</h4>
            {selectedTimeSlotData?.womens_only && <p className="text-sm font-medium text-[#4b27db]">Womens only</p>}
            <p className="text-sm text-gray-600">{provider?.name}</p>
          </div>
          
          <Separator />
          
          {selectedDate && <div>
              <h4 className="font-medium">Date & Time</h4>
              <p className="text-sm text-gray-600">
                {format(selectedDate, "EEEE, MMMM d, yyyy")}
              </p>
              {selectedTimeSlot && getDisplayTime() && <p className="text-sm text-gray-600">
                  {getDisplayTime()}
                </p>}
            </div>}
          
          {selectedDate && selectedTimeSlot && <Separator />}
          
          <div>
            <h4 className="font-medium">Duration</h4>
            <p className="text-sm text-gray-600">{selectedService?.duration_minutes} minutes</p>
          </div>
          
          <Separator />

          {hasActiveWaivers && !waiversCompleted && <>
              <div className="text-center p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-sm text-amber-700 font-medium">
                  Please complete required waivers above before booking
                </p>
              </div>
              <Separator />
            </>}
          
          {selectedMembershipId ? <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span>Service (Membership)</span>
                <span className="text-green-600 font-medium">Included</span>
              </div>
              <div className="flex justify-between items-center font-bold text-lg border-t pt-2">
                <span>Total</span>
                <span className="text-green-600">£0.00</span>
              </div>
              <p className="text-xs text-green-600 text-center">
                Using your membership - no additional charge
              </p>
            </div> : <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span>Service</span>
                <span>£{selectedService?.price?.toFixed(2) || "0.00"}</span>
              </div>
              
              {activePromotion && promotionDiscount > 0 && (
                <div className="flex justify-between items-center text-green-600">
                  <span className="text-sm">
                    Promotion ({activePromotion.name})
                  </span>
                  <span>-£{promotionDiscount.toFixed(2)}</span>
                </div>
              )}
              
              {selectedCoupon && couponDiscount > 0 && (
                <div className="flex justify-between items-center text-green-600">
                  <span className="text-sm">
                    Coupon ({selectedCoupon.coupon.name})
                  </span>
                  <span>-£{couponDiscount.toFixed(2)}</span>
                </div>
              )}
              
              <div className="flex justify-between items-center font-bold text-lg border-t pt-2">
                <span>Total</span>
                <span className={finalPrice === 0 ? "text-green-600" : ""}>
                  £{finalPrice.toFixed(2)}
                </span>
              </div>
              
              {isFreeBooking && (
                <p className="text-xs text-green-600 text-center">
                  Free session using your coupon credits
                </p>
              )}
            </div>}
          
          {/* Updated booking buttons - disabled when date/time not selected but still show error */}
          {selectedMembershipId ? <div className="relative">
              <Button className="w-full bg-green-600 hover:bg-green-700" disabled={!canBookWithMembership || !isDateTimeSelected || processing || penaltyLoading} onClick={e => handleButtonClick('membership', e)}>
                {processing ? "Processing..." : "Book with Membership"}
              </Button>
              {!isDateTimeSelected && <div className="absolute inset-0 cursor-pointer" onClick={e => handleButtonClick('membership', e)} />}
            </div> : isFreeBooking ? <div className="relative">
              <Button className="w-full bg-green-600 hover:bg-green-700" disabled={!canBookRegularly || !isDateTimeSelected || processing || penaltyLoading} onClick={e => handleButtonClick('free', e)}>
                {processing ? "Processing..." : "Confirm Free Booking"}
              </Button>
              {!isDateTimeSelected && <div className="absolute inset-0 cursor-pointer" onClick={e => handleButtonClick('free', e)} />}
            </div> : <div className="relative">
              <Button className="w-full" disabled={!canBookRegularly || !isDateTimeSelected || processing || penaltyLoading} onClick={e => handleButtonClick('regular', e)}>
                {processing ? "Processing..." : "Complete Booking"}
              </Button>
              {!isDateTimeSelected && <div className="absolute inset-0 cursor-pointer" onClick={e => handleButtonClick('regular', e)} />}
            </div>}
          
          <p className="text-xs text-gray-500 text-center">Payment will be processed securely through Stripe</p>
        </CardContent>
      </Card>

      {/* Penalty Payment Modal */}
      {selectedService?.provider_id && <PenaltyPaymentModal open={penaltyModalOpen} onOpenChange={setPenaltyModalOpen} penaltyAmount={penaltyBalance || 0} providerId={selectedService.provider_id} />}
    </>;
};
export default BookingSummary;